import * as React from "react";
import PropTypes from "prop-types";
import {useTheme} from "@mui/material/styles";
import {useNavigate} from "react-router-dom";
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons
} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  Alert,
  AppBar,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
  Stack,
  Chip,
  Box,
  IconButton,
  Paper
} from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import ReplayIcon from "@mui/icons-material/Replay";
import axios from "axios";
import dayjs from "dayjs";
import {BASE_URL} from "APIclient";
import properties from "constants/ApplicationConstants";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import {GET} from "APIclient";
import BlockIcon from "@mui/icons-material/Block";
import EmailIcon from "@mui/icons-material/Email";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import {POST} from "APIclient";
import {formatDesignation} from "constants/ApplicationConstants";
import copyableCell from "components/CopyableCell"

// Custom Pagination Component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666" }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor:
                  properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function PerformanceImprovementList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 20});
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedPipId, setSelectedPipId] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");

  const [searchParams, setSearchParams] = React.useState({
    employeeId: "",
    empName: "",
    pipStartDate: null,
    pipEndDate: null,
    status: ""
  });

  const fetchData = async (page, pageSize, searchParams = {}) => {
    try {
      setLoading(true);
      const startDateFormatted = searchParams.pipStartDate
        ? dayjs(searchParams.pipStartDate).format("YYYY-MM-DD")
        : "";
      const endDateFormatted = searchParams.pipEndDate
        ? dayjs(searchParams.pipEndDate).format("YYYY-MM-DD")
        : "";

      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
        employeeId: searchParams.employeeId || "",
        empName: searchParams.empName || "",
        pipStartDate: startDateFormatted,
        pipEndDate: endDateFormatted,
        managerId: Number(window.sessionStorage.getItem("empId")) || "",
        status: searchParams.status !== null ? searchParams.status.toString() : "1"
      });

      const url = `admin/get-pipList?${queryParams.toString()}`;
      const response = await GET(url);

      if (response.status === 200) {
        const paginatedData = response.data.data.paginatedData || [];
        const totalRecords = response.data.data.totalItems || 0;
        const totalPages = response.data.data.totalPages || 0;

        const formattedData = paginatedData.map((item, index) => ({
          id: page * pageSize + index + 1,
          pipId: item.empPipId,
          employeeId: item.employeeId,
          employeeName: `${item.firstName} ${item.lastName}`.trim(),
          officialEmailAddress: item.officialEmailAddress,
          designation: formatDesignation(item.designation),
          pipStartDate: item.pipStartDate,
          pipEndDate: item.pipEndDate,
          status: item.status
        }));

        setData(formattedData);
        setTotalRows(totalRecords);
        setTotalPages(totalPages);
      } else {
        setData([]);
        setTotalRows(0);
        setTotalPages(0);
        setSnackbar({children: "Failed to fetch data", severity: "error"});
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const message = error?.response?.data?.message || "Failed to resend email.";

      if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage(message);
      } else if (statusCode === 401) {
        setSnakColor("error");
        setSnakMessage("Unauthorized access. Please log in again.");
      } else if (statusCode === 403) {
        setSnakColor("error");
        setSnakMessage("Forbidden. You don’t have permission to perform this action.");
      } else if (statusCode === 404) {
        setSnakColor("error");
        setSnakMessage("API not found. Please contact support.");
      } else if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Conflict. Please check your inputs.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(message);
      }
      setData([]);
      setTotalRows(0);
      setTotalPages(0);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData(page, pageSize, searchParams);
  }, []);

  const handleSearch = () => {
    setPage(0);
    setPaginationModel({page: 0, pageSize});
    fetchData(0, pageSize, searchParams);
  };

  const handlePaginationChange = paginationModel => {
    const newPage = paginationModel.page;
    const newPageSize = paginationModel.pageSize;
    setPage(newPage);
    setPageSize(newPageSize);
    setPaginationModel(paginationModel);
    fetchData(newPage, newPageSize, searchParams);
     setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

    // Updated pagination handlers
  const handlePageChange = (newPage) => {
    const updatedPaginationModel = { ...paginationModel, page: newPage };
    setPaginationModel(updatedPaginationModel);
    fetchData(newPage, paginationModel.pageSize, searchParams);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      const updatedPaginationModel = { ...paginationModel, page: lastPageIndex };
      setPaginationModel(updatedPaginationModel);
      await fetchData(lastPageIndex, paginationModel.pageSize, searchParams);
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  };


  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleProcessRowUpdateError = React.useCallback(error => {
    setSnackbar({children: error.message, severity: "error"});
  }, []);

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };

  const sendMailToEmployee = pipId => {
    setSelectedPipId(pipId);
    setOpenDialog(true);
  };

  const handleResendEmail = async () => {
    try {
      setLoading(true);
      const url = `admin/performance/resend-email?pipId=${selectedPipId}`;
      const response = await POST(url);
      if (response?.status === 200) {
        setSnakColor("success");
        setSnakMessage(response?.data?.message || "Email resent successfully");
        setOpen(true);
      } else {
        setSnakColor("error");
        setSnakMessage("Failed to resend email");
        setOpen(true);
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const message = error?.response?.data?.message || "Failed to resend email.";

      if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage(message);
      } else if (statusCode === 401) {
        setSnakColor("error");
        setSnakMessage("Unauthorized access. Please log in again.");
      } else if (statusCode === 403) {
        setSnakColor("error");
        setSnakMessage("Forbidden. You don’t have permission to perform this action.");
      } else if (statusCode === 404) {
        setSnakColor("error");
        setSnakMessage("API not found. Please contact support.");
      } else if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Conflict. Please check your inputs.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(message);
      }
      setOpen(true);
    } finally {
      setLoading(false);
      setOpenDialog(false);
      setSelectedPipId(null);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedPipId(null);
  };

  const handleViewClick = params => () => {
    navigate("/viewPip", {state: params.pipId});
  };

  const handleEditClick = params => () => {
    setRowModesModel({...rowModesModel, [params.pipId]: {mode: GridRowModes.Edit}});
    navigate("/editPip", {state: params.pipId});
  };

  const handleRevokePip = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
    navigate("/revokePip", {state: id});
  };

  const handlePrintPip = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/downloadCertificate?type=performance_improvement_letter&search=${row.pipId}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });
      if (response.status !== 200) {
        setSnakColor("error");
        setSnakMessage("Failed to fetch letter");
        setOpen(true);
      }
      const fileBlob = new Blob([response.data], {type: "application/pdf"});
      const fileUrl = URL.createObjectURL(fileBlob);
      const newWindow = window.open(fileUrl, "_blank");
      if (!newWindow) {
        alert("Please allow pop-ups to view the PIP letter.");
      }
    } catch (error) {
      alert("Error generating the PIP letter. Please try again.");
    }
  };

  const downloadPip = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/printCertificate?type=performance_improvement_letter&search=${row.pipId}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });
      if (response.status !== 200) {
        throw new Error("Failed to fetch the PDF");
      }
      const fileBlob = new Blob([response.data], {type: "application/pdf"});
      const fileUrl = URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `PIP_Letter_${row.employeeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating PIP letter:", error);
      alert("Error generating the PIP letter. Please try again.");
    }
  };

  const processRowUpdate = newRow => {
    const updatedRow = {...newRow, isNew: false};
    setData(data => data.map(row => (row.pipId === updatedRow.pipId ? updatedRow : row)));
    return updatedRow;
  };

  const formatDate = dateString => {
    if (!dateString) {
      return " - ";
    }
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).format(date);
  };

  const handleSearchChange = event => {
    const {name, value} = event.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: name === "status" ? (value === "" ? null : Number(value)) : value
    }));
  };

  const columns = [
    {field: "id", headerName: "#", width: 30, headerAlign: "center", align: "center"},
    {field: "pipId", headerName: "PIP ID", width: 80, hide: true},
    {
      field: "employeeId",
      headerName: "EMP ID",
      width: 70,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 130,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "officialEmailAddress",
      headerName: "Official Email ID",
      width: 180,
      renderCell: copyableCell
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 150,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "pipStartDate",
      headerName: "PIP Start Date",
      width: 120,
      headerAlign: "left",
      align: "left",
      renderCell: params => formatDate(params.value) || "N/A"
    },
    {
      field: "pipEndDate",
      headerName: "PIP End Date",
      width: 120,
      headerAlign: "left",
      align: "left",
      renderCell: params => formatDate(params.value) || "N/A"
    },
    {
      field: "action",
      headerName: "Action",
      type: "icon",
      width: 140,
      headerAlign: "left",
      align: "left",
      renderCell: params =>
        params.row.status === 1 ? (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{width: "100%", height: "100%"}}
          >
            <Tooltip title="Resend Mail" arrow>
              <GridActionsCellItem
                key={`mail-${params.id}`}
                icon={<EmailIcon />}
                label="Mail"
                color="info"
                onClick={() => sendMailToEmployee(params.row.pipId)}
              />
            </Tooltip>
            <Tooltip title="Extend PIP" arrow>
              <GridActionsCellItem
                key={`extend-${params.id}`}
                icon={<HourglassTopIcon />}
                label="Extend"
                color="warning"
                onClick={() => navigate(`/extendPerformanceImprovement/${params.row.employeeId}`)}
              />
            </Tooltip>
            <Tooltip title="Print" arrow>
              <GridActionsCellItem
                key={`print-${params.id}`}
                icon={<PrintIcon />}
                label="Print"
                color="success"
                onClick={() => handlePrintPip(params.row)}
              />
            </Tooltip>
            <Tooltip title="Download" arrow>
              <GridActionsCellItem
                key={`download-${params.id}`}
                icon={<PictureAsPdf />}
                label="PDF"
                color="primary"
                onClick={() => downloadPip(params.row)}
              />
            </Tooltip>
            <Tooltip title="End PIP" arrow>
              <GridActionsCellItem
                key={`end-${params.id}`}
                icon={<BlockIcon />}
                label="End"
                color="error"
                onClick={() => navigate(`/endPerformanceImprovement/${params.row.employeeId}`)}
              />
            </Tooltip>
          </Stack>
        ) : (
          <Tooltip title="PIP Period Ended" arrow>
            <Chip
              key={`inactive-${params.id}`}
              label="PIP Period End"
              color="warning"
              size="small"
              sx={{
                fontFamily: properties.fontFamily,
                fontWeight: "medium",
                color: "white !important",
                cursor: "default"
              }}
            />
          </Tooltip>
        )
    }
  ];

  const handleClose = () => {
    setSnackbar(null);
    setOpen(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 1.5}}>
                <Typography
                  sx={{
                    gridColumn: "span",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  PIP List
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.employeeId}
                  onChange={handleSearchChange}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="employeeId"
                  label="Employee ID"
                  variant="outlined"
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.empName}
                  onChange={handleSearchChange}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="empName"
                  label="Employee Name"
                  variant="outlined"
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    sx={{gridColumn: "span 2"}}
                    label="PIP Start Date"
                    value={searchParams.pipStartDate}
                    onChange={date => setSearchParams(prev => ({...prev, pipStartDate: date}))}
                    slotProps={{textField: {variant: "outlined", fullWidth: true}}}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    sx={{gridColumn: "span 2"}}
                    label="PIP End Date"
                    value={searchParams.pipEndDate}
                    onChange={date => setSearchParams(prev => ({...prev, pipEndDate: date}))}
                    slotProps={{textField: {variant: "outlined", fullWidth: true}}}
                  />
                </LocalizationProvider>
                <FormControl variant="outlined" sx={{m: 0, width: "15ch", gridColumn: "span 2"}}>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={searchParams.status !== null ? searchParams.status : ""}
                    onChange={handleSearchChange}
                    onKeyDown={event => {
                      if (event.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    label="Status"
                    sx={{minWidth: 150, p: 1.5}}
                  >
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{gridColumn: "span", background: properties.colors.bgcolorbluethem}}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
            <MDBox>
              <Paper>
              <DataGrid
                rows={data}
                columns={columns}
                rowModesModel={rowModesModel}
                getRowId={row => row.pipId}
                columnVisibilityModel={{pipId: false}}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                loading={loading}
                paginationMode="server"
                rowCount={totalRows}
                pageSizeOptions={[20]}
                paginationModel={paginationModel}
                // onPaginationModelChange={handlePaginationChange}
                hideFooter
                sx={{
                  fontFamily: properties.fontFamily,
                  fontSize: "14px",
                  fontWeight:400,
                  height: "450px",
                  "& .MuiDataGrid-scrollbar": {
                    width: "10px !important",
                  },
                }}
              />
              <CustomPagination
                  page={paginationModel.page}
                  pageSize={paginationModel.pageSize}
                  totalRows={totalRows}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onGoToLastPage={handleGoToLastPage}
                  loading={loading}
                />
                </Paper>
            </MDBox>
            <Dialog
              open={openDialog}
              onClose={handleDialogClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle
                id="alert-dialog-title"
                sx={{fontSize: "16px", fontFamily: properties.fontFamily}}
              >
                Confirm Resend Email
              </DialogTitle>
              <DialogContent>
                <DialogContentText
                  id="alert-dialog-description"
                  sx={{fontSize: "14px", fontFamily: properties.fontFamily}}
                >
                  Are you sure you want to resend the PIP email?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleDialogClose}
                  color="primary"
                  fontFamily={properties.fontFamily}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleResendEmail}
                  color="primary"
                  fontFamily={properties.fontFamily}
                  autoFocus
                  disabled={loading}
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
            <Snackbar
              open={open}
              anchorOrigin={{vertical: "top", horizontal: "right"}}
              autoHideDuration={6000}
              onClose={handleClose}
            >
              <Alert
                onClose={handleClose}
                severity={snakColor === "success" ? "success" : "error"}
                variant="filled"
                sx={{width: "100%", color: "#fff"}}
              >
                {snakMessage}
              </Alert>
            </Snackbar>
          
      </MDBox>
    </DashboardLayout>
  );
}

PerformanceImprovementList.propTypes = {
  theme: PropTypes.object
};
